import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DatastoreCollection } from '@freelancer/datastore';
import {
  Experience,
  ExperiencesCollection,
} from '@freelancer/datastore/collections';
import { DurationFormat } from '@freelancer/pipes';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType, TextSize } from '@freelancer/ui/text';
import * as Rx from 'rxjs';

@Component({
  selector: 'app-user-profile-experiences-view',
  template: `
    <fl-bit class="Title">
      <fl-heading
        class="Header"
        [size]="TextSize.SMALL"
        [headingType]="HeadingType.H2"
        [flMarginBottom]="Margin.XSMALL"
      >
        <ng-container *ngIf="experience.title">
          {{ experience.title }}
        </ng-container>
      </fl-heading>

      <app-user-profile-editable-ui-drop-actions
        *ngIf="isViewerProfileOwner$ | flAsync"
        flTrackingLabel="UserProfileExperienceView"
        (delete)="handleActionDelete()"
        (edit)="handleActionEdit()"
      ></app-user-profile-editable-ui-drop-actions>
    </fl-bit>

    <fl-text
      *ngIf="experience.company"
      [fontType]="FontType.STRONG"
      [flMarginBottom]="Margin.XXSMALL"
    >
      {{ experience.company }}
    </fl-text>

    <fl-text [color]="FontColor.MID" [flMarginBottom]="Margin.XXSMALL">
      <ng-container
        *ngIf="
          experience.startDate && experience.endDate && experience.duration
        "
      >
        {{ experience.startDate | dateRange: experience.endDate:'MMM yyyy' }}
        ({{
          experience.duration
            | duration: DurationFormat.LONG:experience.startDate
            | flAsync
        }})
      </ng-container>
      <ng-container *ngIf="experience.startDate && !experience.endDate">
        {{ experience.startDate | date: 'MMM yyyy' }} -
        <ng-container i18n="Present date label">Present</ng-container>
      </ng-container>
      <ng-container *ngIf="!experience.startDate && experience.endDate">
        {{ experience.endDate | date: 'MMM yyyy' }}
      </ng-container>
    </fl-text>

    <fl-text
      *ngIf="experience.description"
      class="ProfileExperiencesDescription"
      [flMarginBottom]="Margin.XXXSMALL"
    >
      {{ experience.description }}
    </fl-text>

    <fl-bit
      *ngIf="
        !(
          experience.title &&
          experience.company &&
          experience.startDate &&
          experience.description
        ) && (isViewerProfileOwner$ | flAsync)
      "
    >
      <fl-text
        *ngIf="!experience.title"
        i18n="Experience title is missing"
        [color]="FontColor.ERROR"
        [flMarginBottom]="Margin.XXXSMALL"
      >
        &bull; Title required
      </fl-text>
      <fl-text
        *ngIf="!experience.company"
        i18n="Experience company is missing"
        [color]="FontColor.ERROR"
        [flMarginBottom]="Margin.XXXSMALL"
      >
        &bull; Company required
      </fl-text>
      <fl-text
        *ngIf="!experience.startDate"
        i18n="Experience start date is missing"
        [color]="FontColor.ERROR"
        [flMarginBottom]="Margin.XXXSMALL"
      >
        &bull; Experience start date required
      </fl-text>
      <fl-text
        *ngIf="!experience.description"
        i18n="Job description is missing"
        [color]="FontColor.ERROR"
        [flMarginBottom]="Margin.XXXSMALL"
      >
        &bull; Job description required
      </fl-text>
      <fl-banner-alert
        *ngIf="!(needsDeleteConfirmation$ | flAsync)"
        [type]="BannerAlertType.ERROR"
      >
        <fl-bit
          i18n="Error message for experience action"
          [flMarginBottom]="Margin.XSMALL"
        >
          Some required data for this entry is missing. Please edit or delete
          this entry to continue.
        </fl-bit>
        <fl-bit>
          <fl-button
            i18n="Edit button"
            flTrackingLabel="UserProfileExperienceViewEditButton"
            [color]="ButtonColor.SECONDARY"
            [flMarginRight]="Margin.XSMALL"
            (click)="handleActionEdit()"
          >
            Edit
          </fl-button>
          <fl-button
            i18n="Delete button"
            flTrackingLabel="UserProfileExperienceViewDeleteButton"
            [color]="ButtonColor.DEFAULT"
            (click)="handleActionDelete()"
          >
            Delete
          </fl-button>
        </fl-bit>
      </fl-banner-alert>
    </fl-bit>

    <fl-banner-alert
      *ngIf="isError$ | flAsync"
      i18n="Error message for experience action"
      [type]="BannerAlertType.ERROR"
    >
      Oops, something went wrong when saving your experience item. Please try
      again.
    </fl-banner-alert>

    <app-user-profile-editable-ui-confirmation
      *ngIf="needsDeleteConfirmation$ | flAsync"
      title="Delete Experience"
      i18n-title="Delete experience confirmation title"
      body="Are you sure you want to delete this experience?"
      i18n-body="Delete experience confirmation body"
      flTrackingLabel="UserProfileExperienceViewDelete"
      [isBusy]="isBusy$ | flAsync"
      (confirm)="handleConfirmDeleteExperience()"
      (cancel)="handleCancelDeleteExperience()"
    ></app-user-profile-editable-ui-confirmation>
  `,
  styleUrls: ['./user-profile-experiences-view.component.scss'],
})
export class UserProfileExperiencesViewComponent {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  DurationFormat = DurationFormat;
  FontColor = FontColor;
  FontType = FontType;
  TextSize = TextSize;
  HeadingType = HeadingType;
  Margin = Margin;

  @Input() experience: Experience;
  @Input() experiencesCollection: DatastoreCollection<ExperiencesCollection>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;
  // Draft experiences are experiences not saved to the backend.
  // Do not update datastore collection with draft experiences. Return it with modified fields.
  @Input() isDraftExperience = false;
  @Output() edit = new EventEmitter<Experience>();
  @Output() deleteDraftExperience = new EventEmitter<Experience>();

  private _needsDeleteConfirmation$ = new Rx.BehaviorSubject<boolean>(false);
  needsDeleteConfirmation$ = this._needsDeleteConfirmation$.asObservable();

  private _isBusy$ = new Rx.BehaviorSubject<boolean>(false);
  isBusy$ = this._isBusy$.asObservable();

  private _isError$ = new Rx.BehaviorSubject<boolean>(false);
  isError$ = this._isError$.asObservable();

  handleActionEdit(): void {
    this.edit.emit(this.experience);
  }

  handleActionDelete(): void {
    this._needsDeleteConfirmation$.next(true);
  }

  handleConfirmDeleteExperience(): void {
    this._isBusy$.next(true);

    if (this.isDraftExperience) {
      this.deleteDraftExperience.emit(this.experience);
      this._needsDeleteConfirmation$.next(false);
      this._isBusy$.next(false);
    } else {
      this.experiencesCollection.remove(this.experience.id).then(response => {
        this._needsDeleteConfirmation$.next(false);
        this._isBusy$.next(false);

        if (response.status === 'error') {
          this._isError$.next(true);
        }
      });
    }
  }

  handleCancelDeleteExperience(): void {
    this._needsDeleteConfirmation$.next(false);
  }
}
